import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Settings, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function SettingsManagement({ user }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deployMin, setDeployMin] = useState(13);
    const [reshapeMin, setReshapeMin] = useState(9);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/admin/settings/dri`, { withCredentials: true });
            if (response.data) {
                setDeployMin(response.data.deploy_min || 13);
                setReshapeMin(response.data.reshape_min || 9);
            }
        } catch (error) {
            toast.error('Erro ao carregar configurações DRI');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Validation
        const dMin = parseInt(deployMin, 10);
        const rMin = parseInt(reshapeMin, 10);
        
        if (isNaN(dMin) || isNaN(rMin) || dMin <= rMin || rMin <= 0 || dMin > 15) {
            toast.error('Valores inválidos. Deploy > Reshape > 0. E máximo 15.');
            return;
        }

        try {
            setSaving(true);
            await axios.put(`${API}/admin/settings/dri`, {
                deploy_min: dMin,
                reshape_min: rMin
            }, { withCredentials: true });
            
            toast.success('Configurações salvas com sucesso!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
        } finally {
            setSaving(false);
        }
    };

    if (user?.role !== 'super_admin' && user?.role !== 'admin') {
        return (
            <Card className="p-8 rounded-none border border-border text-center">
                <Settings className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Apenas Administradores podem gerenciar configurações globais.</p>
            </Card>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Configurações Globais
                </h2>
            </div>

            <div className="grid gap-6">
                <Card className="p-6 rounded-none border border-border">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-none">
                            <Settings className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Classificação Automática DRI (Score)</h3>
                            <p className="text-xs text-muted-foreground">
                                Configure as notas de corte para a auto-classificação das iniciativas no Plano AI First. A pontuação máxima teórica (Valor + Viabilidade + Risco) é 15.
                            </p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center p-4">Carregando...</div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                                
                                {/* DEPLOY Settings */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-bold uppercase tracking-wide text-foreground flex items-center gap-2">
                                            <span className="w-2 h-2 bg-aift-blue rounded-full"></span>
                                            DEPLOY (&lt; 6 meses)
                                        </label>
                                    </div>
                                    <div className="bg-muted/30 p-4 border border-border space-y-2">
                                        <p className="text-xs text-muted-foreground mb-3">
                                            Será considerado DEPLOY se Score Final for <strong className="text-foreground">&ge; a:</strong>
                                        </p>
                                        <Input
                                            type="number"
                                            value={deployMin}
                                            onChange={(e) => setDeployMin(e.target.value)}
                                            min={reshapeMin + 1}
                                            max={15}
                                            className="font-mono"
                                        />
                                        <p className="text-[10px] text-muted-foreground">O intervalo atual ficará: {deployMin} até 15</p>
                                    </div>
                                </div>

                                {/* RESHAPE Settings */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-bold uppercase tracking-wide text-foreground flex items-center gap-2">
                                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                            RESHAPE (6-12 meses)
                                        </label>
                                    </div>
                                    <div className="bg-muted/30 p-4 border border-border space-y-2">
                                        <p className="text-xs text-muted-foreground mb-3">
                                            Será considerado RESHAPE se Score Final for <strong className="text-foreground">&ge; a:</strong>
                                        </p>
                                        <Input
                                            type="number"
                                            value={reshapeMin}
                                            onChange={(e) => setReshapeMin(e.target.value)}
                                            min={1}
                                            max={deployMin - 1}
                                            className="font-mono"
                                        />
                                        <p className="text-[10px] text-muted-foreground">O intervalo atual ficará: {reshapeMin} até {deployMin - 1}</p>
                                        <p className="text-[10px] text-muted-foreground mt-2 border-t border-border pt-2 italic">
                                            * INVENT ficará automaticamente definido como &lt; {reshapeMin}.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-2 bg-blue-500/10 text-blue-500 p-4 mt-6">
                                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                <p className="text-sm">Alterar essas pontuações afetará como as <strong>novas classificações automáticas</strong> são sugeridas no Plano AI First.Iniciativas já salvos com um DRI manual não são alteradas automaticamente no banco de dados.</p>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-border">
                                <Button 
                                    onClick={handleSave} 
                                    disabled={saving || loading}
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-none uppercase text-xs tracking-wider px-6"
                                >
                                    {saving ? 'Salvando...' : (
                                        <>
                                            <Save className="w-4 h-4 mr-2" /> 
                                            Salvar Configurações Globais
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
